import { apiClient } from './ApiClient'

export interface OrganisationSummary {
  customerId: string
  companyName: string
  contactEmail: string
  accountStatus: string
  billingPlan: string
  createdAt: string
  userCount: number
  ticketCount: number
  openTicketCount: number
  pendingTicketCount: number
  closedTicketCount: number
  departmentCount: number
  isActive: boolean
  isCurrentUserOrganization: boolean
  tenantSlug: string
  emailCode?: string
  emailAddress?: string
  parentOrganizationId?: string
}

export interface OrganisationInfo {
  customerId: string
  companyName: string
  contactEmail: string
  accountStatus: string
  trialEndsAt: string
  createdAt: string
  billingPlan: string
  monthlyAmount: number
  maxAgents: number
  isActive: boolean
  notes?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  stateCounty?: string
  postalCode?: string
  country?: string
  tenantId: string
  tenantSlug: string
  emailCode?: string
  emailAddress?: string
  userCount: number
  agentCount: number
  ticketCount: number
  openTicketCount: number
}

export interface CreateOrganisationRequest {
  companyName: string
  contactEmail: string
  notes?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  stateCounty?: string
  postalCode?: string
  country?: string
}

export interface UpdateOrganisationRequest {
  companyName: string
  contactEmail: string
  accountStatus: string
  billingPlan: string
  maxAgents: number
  monthlyAmount: number
  isActive: boolean
  notes?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  stateCounty?: string
  postalCode?: string
  country?: string
}

export interface OrganisationSlaOverride {
  enabled: boolean
  policies?: {
    [key: string]: {
      responseHours: number
      resolutionHours: number
    }
  }
}

export class OrganisationsService {
  private basePath = '/organisations'

  async getOrganisations(): Promise<OrganisationSummary[]> {
    return apiClient.get<OrganisationSummary[]>(this.basePath)
  }

  async getOrganisation(id: string): Promise<OrganisationInfo> {
    return apiClient.get<OrganisationInfo>(`${this.basePath}/${id}`)
  }

  async createOrganisation(request: CreateOrganisationRequest): Promise<OrganisationInfo> {
    return apiClient.post<OrganisationInfo>(this.basePath, request)
  }

  async updateOrganisation(id: string, request: UpdateOrganisationRequest): Promise<OrganisationInfo> {
    return apiClient.put<OrganisationInfo>(`${this.basePath}/${id}`, request)
  }

  async activateOrganisation(id: string): Promise<void> {
    return apiClient.post<void>(`${this.basePath}/${id}/activate`)
  }

  async deactivateOrganisation(id: string): Promise<void> {
    return apiClient.post<void>(`${this.basePath}/${id}/deactivate`)
  }

  async getOrganizationSla(organizationId: string): Promise<OrganisationSlaOverride> {
    return apiClient.get<OrganisationSlaOverride>(`${this.basePath}/${organizationId}/sla`)
  }

  async updateOrganizationSla(organizationId: string, override: OrganisationSlaOverride): Promise<OrganisationSlaOverride> {
    return apiClient.put<OrganisationSlaOverride>(`${this.basePath}/${organizationId}/sla`, override)
  }
}

export const organisationsService = new OrganisationsService()
